import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const api = axios.create({
  baseURL: API,
  withCredentials: true,
  headers: { 'Content-Type': 'application/json' }
});

// Auth
export const exchangeSession = (session_id) => api.post('/auth/session', { session_id });
export const getMe = () => api.get('/auth/me');
export const logout = () => api.post('/auth/logout');

// Families
export const getFamilies = () => api.get('/families');
export const createFamily = (data) => api.post('/families', data);
export const updateFamily = (id, data) => api.put(`/families/${id}`, data);
export const getFamilyMembers = (id) => api.get(`/families/${id}/members`);
export const inviteMember = (familyId, data) => api.post(`/families/${familyId}/invite`, data);

// Trips
export const getTrips = (familyId) => api.get('/trips', { params: { family_id: familyId } });
export const createTrip = (data) => api.post('/trips', data);
export const aiWizardCreateTrip = (data) => api.post('/trips/ai-wizard', data);
export const aiWizardSuggestDestinations = (query) => api.post('/trips/ai-wizard/suggest-destinations', { query });
export const getTrip = (id) => api.get(`/trips/${id}`);
export const updateTrip = (id, data) => api.put(`/trips/${id}`, data);
export const deleteTrip = (id) => api.delete(`/trips/${id}`);

// Travelers
export const getTravelers = (tripId) => api.get(`/trips/${tripId}/travelers`);
export const addTraveler = (tripId, data) => api.post(`/trips/${tripId}/travelers`, data);
export const updateTraveler = (id, data) => api.put(`/travelers/${id}`, data);
export const removeTraveler = (tripId, travelerId) => api.delete(`/trips/${tripId}/travelers/${travelerId}`);

// Bookings
export const getBookings = (tripId) => api.get(`/trips/${tripId}/bookings`);
export const addBooking = (tripId, data) => api.post(`/trips/${tripId}/bookings`, data);
export const updateBooking = (id, data) => api.put(`/bookings/${id}`, data);
export const toggleLockBooking = (id) => api.post(`/bookings/${id}/lock`);
export const importBookings = (tripId, file) => {
  const formData = new FormData();
  formData.append('file', file);
  return api.post(`/trips/${tripId}/bookings/import`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  });
};

// Tasks
export const getTasks = (params) => api.get('/tasks', { params });
export const createTask = (data) => api.post('/tasks', data);
export const updateTask = (id, data) => api.put(`/tasks/${id}`, data);
export const deleteTask = (id) => api.delete(`/tasks/${id}`);

// Compliance
export const getCompliance = (tripId) => api.get(`/trips/${tripId}/compliance`);
export const addCompliance = (tripId, data) => api.post(`/trips/${tripId}/compliance`, data);
export const updateCompliance = (id, data) => api.put(`/compliance/${id}`, data);

// Expenses
export const getExpenses = (tripId) => api.get(`/trips/${tripId}/expenses`);
export const addExpense = (tripId, data) => api.post(`/trips/${tripId}/expenses`, data);
export const updateExpense = (id, data) => api.put(`/expenses/${id}`, data);
export const deleteExpense = (id) => api.delete(`/expenses/${id}`);
export const getBudget = (tripId) => api.get(`/trips/${tripId}/budget`);

// Day Plans & Activities
export const getDayPlans = (tripId) => api.get(`/trips/${tripId}/day-plans`);
export const updateDayPlan = (id, data) => api.put(`/day-plans/${id}`, data);
export const addActivity = (dayPlanId, data) => api.post(`/day-plans/${dayPlanId}/activities`, data);
export const updateActivity = (id, data) => api.put(`/activities/${id}`, data);
export const deleteActivity = (id) => api.delete(`/activities/${id}`);
export const reorderActivities = (dayPlanId, activities) => api.post(`/day-plans/${dayPlanId}/reorder`, { activities });

// Transport Segments
export const addTransportSegment = (dayPlanId, data) => api.post(`/day-plans/${dayPlanId}/transport`, data);
export const updateTransportSegment = (segmentId, data) => api.put(`/transport/${segmentId}`, data);
export const deleteTransportSegment = (id) => api.delete(`/transport/${id}`);

// Google Maps
export const getDirections = (origin, destination, mode) => api.get('/maps/directions', { params: { origin, destination, mode } });
export const searchPlaces = (query, location) => api.get('/maps/places', { params: { query, location } });

// AI Copilot
export const copilotAnalyze = (tripId) => api.post(`/trips/${tripId}/copilot/analyze`);
export const copilotChat = (tripId, message) => api.post(`/trips/${tripId}/copilot/chat`, { message, trip_id: tripId });
export const getCopilotHistory = (tripId) => api.get(`/trips/${tripId}/copilot/history`);

// Stress Score
export const getStressScore = (dayPlanId) => api.get(`/day-plans/${dayPlanId}/stress-score`);

// Incidents
export const getIncidents = (tripId) => api.get(`/trips/${tripId}/incidents`);
export const createIncident = (tripId, data) => api.post(`/trips/${tripId}/incidents`, data);
export const updateIncident = (id, data) => api.put(`/incidents/${id}`, data);
export const replanIncident = (id) => api.post(`/incidents/${id}/replan`);
export const createCompensationClaim = (incidentId, data) => api.post(`/incidents/${incidentId}/claims`, data);

// Compliance Alerts
export const getComplianceAlerts = (tripId) => api.get(`/trips/${tripId}/compliance/alerts`);
export const aiComplianceCheck = (tripId) => api.post(`/trips/${tripId}/compliance/ai-check`);

// Dashboard Agenda
export const getTodayAgenda = () => api.get('/dashboard/agenda');

// Budget Chart
export const getBudgetChart = (tripId) => api.get(`/trips/${tripId}/budget/chart`);

// Share Links
export const createShareLink = (tripId, data) => api.post(`/trips/${tripId}/share`, data);
export const getShareLinks = (tripId) => api.get(`/trips/${tripId}/share`);
export const revokeShareLink = (id) => api.delete(`/share/${id}`);
export const getSharedTrip = (linkId, pin) => axios.get(`${API}/public/share/${linkId}`, { params: { pin } });

// Health
export const getHealthItems = (tripId) => api.get(`/trips/${tripId}/health`);
export const addHealthItem = (tripId, data) => api.post(`/trips/${tripId}/health`, data);
export const toggleHealthItem = (id) => api.put(`/health/${id}/toggle`);
export const deleteHealthItem = (id) => api.delete(`/health/${id}`);
export const aiHealthCheck = (tripId) => api.post(`/trips/${tripId}/health/ai-check`);

// Journal
export const getJournal = (tripId) => api.get(`/trips/${tripId}/journal`);
export const addJournalEntry = (tripId, data) => api.post(`/trips/${tripId}/journal`, data);
export const updateJournalEntry = (id, data) => api.put(`/journal/${id}`, data);
export const deleteJournalEntry = (id) => api.delete(`/journal/${id}`);
export const generateAiMemory = (tripId, entryId) => api.post(`/trips/${tripId}/journal/${entryId}/ai-memory`);

// Visit Companion
export const getPoiResources = (tripId) => api.get(`/trips/${tripId}/visit-companion`);
export const generateQuiz = (tripId) => api.post(`/trips/${tripId}/quiz/generate`);
export const getQuiz = (tripId) => api.get(`/trips/${tripId}/quiz`);
export const generateAudioGuide = (poiId, language, mode, audience = 'adult') => api.post(`/poi/${poiId}/audio-guide`, { language, mode, audience, generate_audio: true });
export const generateActivityAudioGuide = (activityId, language, audience = 'adult') => api.post(`/activity/${activityId}/audio-guide`, { language, audience });

// Start background audio generation (returns immediately)
export const startAudioGeneration = (poiId, language, mode, audience = 'adult') => api.post(`/poi/${poiId}/audio-guide/start`, { language, mode, audience });
// Check audio generation status
export const checkAudioStatus = (poiId) => api.get(`/poi/${poiId}/audio-guide/status`);

// Daily Digest
export const generateDigest = (tripId) => api.post(`/trips/${tripId}/digest/generate`);
export const getDigests = (tripId) => api.get(`/trips/${tripId}/digests`);

// Voice Copilot
export const voiceCopilot = (tripId, query) => api.post(`/trips/${tripId}/voice`, { query });

// Receipt Scanner
export const scanReceipt = (tripId, file) => {
  const formData = new FormData();
  formData.append('file', file);
  return api.post(`/trips/${tripId}/receipts/scan`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  });
};
export const getReceipts = (tripId) => api.get(`/trips/${tripId}/receipts`);

// Google Photos
export const connectGooglePhotos = (tripId) => api.get(`/trips/${tripId}/google-photos/connect`);
export const getGooglePhotosAlbums = (tripId) => api.get(`/trips/${tripId}/google-photos/albums`);
export const importGooglePhotosAlbum = (tripId, albumId) => api.post(`/trips/${tripId}/google-photos/import-album`, { album_id: albumId });
export const getTripPhotos = (tripId) => api.get(`/trips/${tripId}/photos`);

// Instant Pulse to Itinerary
export const addInstantPulseToItinerary = (data) => api.post('/instant-pulse/add-to-itinerary', data);

// Instant Translation
export const instantTranslate = (text, targetLang, sourceLang, context) => 
  api.post('/instant-pulse/translate', { 
    text, 
    target_lang: targetLang, 
    source_lang: sourceLang, 
    context 
  });

// Instant Conversion
export const instantConvert = (value, fromUnit, toUnit, category) => 
  api.post('/instant-pulse/convert', { 
    value, 
    from_unit: fromUnit, 
    to_unit: toUnit, 
    category 
  });

// Get Instant Tools
export const getInstantTools = () => api.get('/instant-pulse/tools');

// User Roles
export const getMyRole = (tripId) => api.get(`/trips/${tripId}/my-role`);
export const switchMode = (tripId, mode) => api.post(`/trips/${tripId}/switch-mode`, { mode });
export const assignRole = (tripId, userId, role) => api.post(`/trips/${tripId}/assign-role`, { user_id: userId, role });
export const grantPermissions = (tripId, userId, permissions) => api.post(`/trips/${tripId}/grant-permissions`, { user_id: userId, permissions });
export const getTripUsersRoles = (tripId) => api.get(`/trips/${tripId}/users-roles`);


// Trip Invitations
export const inviteTraveler = (tripId, email, name, role) => api.post(`/trips/${tripId}/invite`, { email, name, role });
export const getTripInvitations = (tripId) => api.get(`/trips/${tripId}/invitations`);

// Day AI Chat
export const sendDayAIChat = (dayPlanId, message, sessionId = null) => api.post(`/day-plans/ai-chat`, { day_plan_id: dayPlanId, message, session_id: sessionId });
export const getDayAIChatHistory = (dayPlanId) => api.get(`/day-plans/${dayPlanId}/ai-chat-history`);

// Active Trip
export const getActiveTrip = () => api.get('/trips/active');

// Dashboard Alerts
export const getDashboardAlerts = () => api.get('/dashboard/alerts');

// Quick Deck - Generate recommendations
export const generateQuickPlan = (params) => api.post('/quickdeck/generate', params);

// Scan Here - Identify location from image
export const identifyLocation = (data) => api.post('/scan/identify', data);

// Scan Here - Get nearby places via GPS
export const getNearbyPlaces = (coords) => api.post('/scan/nearby', coords);

// FX Rates
export const getFxRates = (base) => api.get('/fx/rates', { params: { base } });

// User
export const updateUser = (data) => api.put('/users/me', data);

// GDPR
export const gdprExport = () => api.get('/gdpr/export');
export const gdprDeleteAccount = () => api.delete('/gdpr/delete-account');
export const getAuditLogs = (limit) => api.get('/audit-logs', { params: { limit } });

// Push Notifications
export const subscribePush = (subscription, familyId) => api.post('/push/subscribe', { subscription, family_id: familyId });
export const unsubscribePush = () => api.delete('/push/unsubscribe');
export const getPushStatus = () => api.get('/push/status');
export const getPendingNotifications = () => api.get('/push/pending');
export const getPushPreferences = () => api.get('/push/preferences');
export const updatePushPreferences = (preferences) => api.put('/push/preferences', preferences);

// Email
export const sendInviteEmail = (data) => api.post('/email/invite', data);

// Favorites
export const getFavorites = () => api.get('/favorites');
export const addFavorite = (data) => api.post('/favorites', data);
export const removeFavorite = (placeId) => api.delete(`/favorites/${placeId}`);
export const checkFavorite = (placeId) => api.get(`/favorites/check/${placeId}`);

// Enhanced Audio Guide
export const getAudioVersions = (lang, poiName) => api.get('/audio/versions', { params: { lang, poi_name: poiName } });
export const generateEnhancedAudioGuide = (data) => api.post('/audio/enhanced-guide', data);
export const generateChapterAudio = (chapterNum, data) => api.post(`/audio/generate-chapter/${chapterNum}`, data);

// Instant Pulse Audio (uses full pipeline with ambiance + random voices)
export const startInstantPulseAudio = (data) => api.post('/instant-pulse/audio/start', data);
export const checkInstantPulseAudioStatus = (poiId) => api.get(`/instant-pulse/audio/status/${poiId}`);

export default api;
